import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UserMsg } from '../models/user'

interface StateProps {
    user: string | null,
    msg: UserMsg | null
}

const initialState: StateProps = {
    user: null,
    msg: null
}

interface UserAction {
    user?: string | null
    msg?: UserMsg | null
}

export const logout = createAsyncThunk(
    'user/logout',
    async () => {
        // Revoke the refresh token server-side before clearing local state
        const clientId = process.env.REACT_APP_USERPOOL_CLIENT_ID!
        const domain = process.env.REACT_APP_USERPOOL_DOMAIN
        try {
            const prefix = `CognitoIdentityServiceProvider.${clientId}`
            const lastUser = localStorage.getItem(`${prefix}.LastAuthUser`)
            const refreshToken = lastUser && localStorage.getItem(`${prefix}.${lastUser}.refreshToken`)
            if (refreshToken) {
                await fetch(`https://${domain}/oauth2/revoke`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: `token=${encodeURIComponent(refreshToken)}&client_id=${clientId}`,
                }).catch(() => {})
            }
        } catch {
            // Continue with logout even if revocation fails
        }

        // Clear Amplify auth tokens from local storage
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('CognitoIdentityServiceProvider.') || key.startsWith('amplify-')) {
                localStorage.removeItem(key)
            }
        })
        // Cognito Managed Login requires `redirect_uri`, but `logout_uri` controls the actual redirect destination
        const redirectUri = encodeURIComponent(`${window.location.origin}/`)
        const logoutUri = encodeURIComponent(`${window.location.origin}/login`)
        window.location.href = `https://${domain}/logout?client_id=${clientId}&redirect_uri=${redirectUri}&logout_uri=${logoutUri}&response_type=code`
    }
)


export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setLoggedInUser(state, action: PayloadAction<string>) {
            state.user = action.payload
        },
        setUserMsg(state, action: PayloadAction<UserAction>) {
            state.msg = action.payload.msg!
        }
    },
    extraReducers: (builder) => {
        builder.addCase(logout.fulfilled, (state, action) => {
            state.user = null
        })
        builder.addCase(logout.rejected, (state) => {
            const msg: UserMsg = { type: 'danger', txt: 'Failed logout. Please try again later' }
            const userAction = { type: 'setUserMsg', payload: { msg } }
            userSlice.caseReducers.setUserMsg(state, userAction)
        })
    }
})

export const { setUserMsg, setLoggedInUser } = userSlice.actions